import { useSyncExternalStore } from 'react'

/*
  useMediaQuery — subscribe to a CSS media query and re-render on change.

  One MediaQueryList per distinct query is cached module-wide and shared by all
  subscribers through useSyncExternalStore, so a list where every row calls
  useIsCompact() adds N cheap subscriptions to ONE listener, not N listeners.
  Client-only Vite SPA (no SSR), so matchMedia always exists. Used where layout
  lives in inline style={{}} objects that can't carry a media query (the shell
  drawer, the dense Stream/Inspector grids). Pure className layout should still
  use Tailwind sm:/md: prefixes.
*/
const mqlCache = new Map<string, MediaQueryList>()

function getMql(query: string): MediaQueryList {
  let mql = mqlCache.get(query)
  if (!mql) {
    mql = window.matchMedia(query)
    mqlCache.set(query, mql)
  }
  return mql
}

export function useMediaQuery(query: string): boolean {
  return useSyncExternalStore(
    (onChange) => {
      const mql = getMql(query)
      mql.addEventListener('change', onChange)
      return () => mql.removeEventListener('change', onChange)
    },
    () => getMql(query).matches,
  )
}

// Below md (768px) the shell collapses its docked sidebar into a drawer. Keep
// this threshold in lockstep with the `md:` Tailwind prefix used for the shell's
// show/hide so the JS behaviour (focus trap, inert) and CSS visibility agree.
export function useIsMobile(): boolean {
  return useMediaQuery('(max-width: 767px)')
}

// Below sm (640px) dense desktop tables/grids reflow to stacked layouts.
export function useIsCompact(): boolean {
  return useMediaQuery('(max-width: 639px)')
}

// True only on a hover-capable, precise pointer (mouse). When false (touch),
// hover-revealed row actions must be rendered persistently instead of gated.
export function useHasHover(): boolean {
  return useMediaQuery('(hover: hover) and (pointer: fine)')
}
